import React, { useCallback, useId, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getExtensionsFromAccept } from '../../core/utils/file-utils';

interface FileDropzoneProps {
  accept: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  label?: string;
  dropzoneText?: string;
}

export const FileDropzone = ({
  accept,
  file,
  onFileChange,
  label,
  dropzoneText
}: FileDropzoneProps) => {
  const t = useTranslate();
  const [dragOver, setDragOver] = useState(false);
  const inputId = useId();

  const allowedExtensions = getExtensionsFromAccept(accept);

  const handleFile = useCallback(
    (f: File) => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (allowedExtensions.includes(ext || '')) {
        onFileChange(f);
      }
    },
    [allowedExtensions, onFileChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [handleFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFile(e.target.files[0]);
      }
    },
    [handleFile]
  );

  return (
    <Box sx={{ maxWidth: 500, width: '100%' }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => document.getElementById(inputId)?.click()}
        sx={{
          border: '1px dashed',
          borderColor: dragOver ? 'primary.main' : 'grey.400',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: dragOver ? 'action.hover' : 'transparent',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {dropzoneText || t('resources.pumps.speeds.fields.dropzone')}
        </Typography>
        <input
          id={inputId}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Box>

      {file && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 1,
            gap: 1
          }}
        >
          <IconButton
            size="small"
            onClick={() => onFileChange(null)}
            color="error"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2">{file.name}</Typography>
        </Box>
      )}
    </Box>
  );
};
